import type { OpenClawApp } from "./app.ts";
import type { SessionArtifactSummary } from "./chat/chat-file-changes-summary.ts";
import { getChatRunArtifactsSinceTs } from "./chat/chat-run-artifacts.ts";
import { resolveAgentIdForSession } from "./chat/resolve-session-agent.ts";
import {
  mergeRunArtifactPreview,
  parseSessionArtifactRecorded,
  type RunArtifactPreviewEntry,
} from "./controllers/run-artifact-previews.ts";

export function runArtifactPreviewsForSession(
  app: OpenClawApp,
  sessionKey: string,
): RunArtifactPreviewEntry[] {
  const key = sessionKey.trim();
  if (!key) {
    return [];
  }
  return app.runArtifactPreviewsBySession[key] ?? [];
}

export function clearRunArtifactPreviews(app: OpenClawApp, sessionKey?: string): void {
  if (sessionKey?.trim()) {
    const key = sessionKey.trim();
    if (!app.runArtifactPreviewsBySession[key]) {
      return;
    }
    const next = { ...app.runArtifactPreviewsBySession };
    delete next[key];
    app.runArtifactPreviewsBySession = next;
    return;
  }
  app.runArtifactPreviewsBySession = {};
}

export function applySessionArtifactRecordedEvent(app: OpenClawApp, payload: unknown): void {
  const parsed = parseSessionArtifactRecorded(payload);
  if (!parsed) {
    return;
  }
  const { sessionKey, entry } = parsed;
  const existing = app.runArtifactPreviewsBySession[sessionKey] ?? [];
  app.runArtifactPreviewsBySession = {
    ...app.runArtifactPreviewsBySession,
    [sessionKey]: mergeRunArtifactPreview(existing, entry),
  };
}

export async function hydrateRunArtifactPreviews(app: OpenClawApp): Promise<void> {
  if (!app.client || !app.connected) {
    return;
  }
  const sessionKey = app.sessionKey.trim();
  const runActive = Boolean(app.chatRunId) || app.chatSending || app.chatStream !== null;
  if (!sessionKey || !runActive) {
    return;
  }
  const sinceTs = getChatRunArtifactsSinceTs(app.chatRunArtifactsSinceTs, sessionKey);
  const agentId = resolveAgentIdForSession(sessionKey);
  try {
    const summary = await app.client.request<SessionArtifactSummary>("sessions.artifacts.summary", {
      agentId,
      sessionKey,
      ...(sinceTs != null ? { sinceTs } : {}),
    });
    if (!summary?.files?.length) {
      return;
    }
    const entries: RunArtifactPreviewEntry[] = summary.files
      .filter((f) => f.changeId)
      .map((f) => ({
        changeId: f.changeId!,
        path: f.path,
        op: f.op,
        additions: f.additions,
        deletions: f.deletions,
        sessionKey,
        previewLines: f.previewLines ?? [],
      }));
    if (entries.length === 0) {
      return;
    }
    let merged = app.runArtifactPreviewsBySession[sessionKey] ?? [];
    for (const entry of entries) {
      merged = mergeRunArtifactPreview(merged, entry);
    }
    app.runArtifactPreviewsBySession = {
      ...app.runArtifactPreviewsBySession,
      [sessionKey]: merged,
    };
  } catch {
    // best-effort hydrate on reconnect
  }
}
